import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Calendar, Check, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import showAlert, {
  showAlertWithConfirmCancel,
} from "../../../server/ShowAlert";
import {
  GetSupplierPrices,
  UpdateSupplierPrice,
  deleteSupplierPromotion,
  formatSupplierPriceDate,
} from "../../../server/SupplierPrice";
import FamilyChangeDialog from "./FamilyChangeDialog";
import { applySequentially } from "./useFamilyProductUpdate";
import {
  discountPercentFromPrices,
  getDepositAmount,
  getManufacturerPackPurchasePrice,
  getPriceExcludingDeposit,
  getPriceIncludingDeposit,
  getPurchasePriceFromManufacturerPack,
  isSamePriceValue,
  isValidDiscountPercent,
  priceAfterDiscount,
} from "./priceMarginUtils";
import { getManufacturerPackQuantity } from "../sales/promotionUtils";
import { formatPrice, roundMoney } from "../../../utils/formatPrice";
import { formatExpirationDate } from "../../../utils/formatExpirationDate";
import PopUp from "../../../components/containers/PopUp";
import SupplierPromotionForm, {
  SUPPLIER_PROMO_TYPES,
  getSupplierPromoTypeName,
  getSupplierPromoValue,
} from "./SupplierPromotionForm";

const normalizePriceList = (data) => {
  if (!data) return [];
  return Array.isArray(data) ? data : [data];
};

const toCostForMargin = (basePrice, depositAmount, vatOptions = {}) => {
  const base = parseFloat(basePrice);
  if (!Number.isFinite(base)) return null;
  return depositAmount
    ? getPriceIncludingDeposit(base, depositAmount, vatOptions)
    : roundMoney(base);
};

const toDiscountNumber = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

const getPromoDedupeKey = (promo) =>
  promo?.promoId ||
  [
    promo?.promoType,
    promo?.discountValue,
    promo?.minQuantity ?? "",
    promo?.startDate ?? "",
    promo?.endDate ?? "",
  ].join("|");

const dedupeSupplierPromotions = (promotions) => {
  const byKey = new Map();
  for (const promo of promotions || []) {
    const key = getPromoDedupeKey(promo);
    if (!byKey.has(key)) byKey.set(key, promo);
  }
  return [...byKey.values()];
};

const SUPPLIER_PROMO_STATUS_MAP = {
  ACTIVE: { label: "פעיל", modifier: "active" },
  SCHEDULED: { label: "מתוזמן", modifier: "scheduled" },
  EXPIRED: { label: "פג תוקף", modifier: "expired" },
  DISABLED: { label: "מוקפא", modifier: "disabled" },
};

const getSupplierPromoDateRange = (promo) => {
  const fmt = (d) => formatExpirationDate(d) || "";
  const start = fmt(promo?.startDate);
  const end = fmt(promo?.endDate);
  if (start && end) return `${start} — ${end}`;
  if (start) return `מ-${start}`;
  if (end) return `עד ${end}`;
  return "";
};

const isActivePromoStatus = (status) => (status ?? "ACTIVE") === "ACTIVE";

const countActivePromotions = (promotions = []) =>
  promotions.filter((promo) => isActivePromoStatus(promo?.status)).length;

const getActiveSupplierPromotion = (promotions = []) =>
  promotions.find((promo) => isActivePromoStatus(promo?.status)) || null;

const getPurchasePriceIncludingPromos = (afterDiscount, promo) => {
  if (!promo) {
    const n = parseFloat(afterDiscount);
    return Number.isFinite(n) ? roundMoney(n) : null;
  }

  switch (promo.promoType) {
    case SUPPLIER_PROMO_TYPES.FIXED_PRICE: {
      const value = parseFloat(promo.discountValue);
      return Number.isFinite(value) ? roundMoney(value) : null;
    }
    case SUPPLIER_PROMO_TYPES.PERCENT:
      return priceAfterDiscount(afterDiscount, promo.discountValue);
    case SUPPLIER_PROMO_TYPES.X_FOR_Y: {
      const qty = parseFloat(promo.minQuantity);
      const total = parseFloat(promo.discountValue);
      if (!Number.isFinite(qty) || qty <= 0 || !Number.isFinite(total)) {
        return null;
      }
      return roundMoney(total / qty);
    }
    default: {
      const n = parseFloat(afterDiscount);
      return Number.isFinite(n) ? roundMoney(n) : null;
    }
  }
};

const tabIdForPanel = (panelId) => `${panelId}-tab`;

const PricesBlockTabs = ({
  label,
  value,
  onChange,
  pricesPanelId,
  offersPanelId,
  offersCount = 0,
}) => {
  const offersLabel = offersCount > 0 ? `מבצעים (${offersCount})` : "מבצעים";
  const pricesTabId = tabIdForPanel(pricesPanelId);
  const offersTabId = tabIdForPanel(offersPanelId);

  return (
    <div className="prices-block-tabs" role="tablist" aria-label={label}>
      <button
        type="button"
        role="tab"
        id={pricesTabId}
        aria-selected={value === "prices"}
        aria-controls={pricesPanelId}
        className={`prices-block-tabs__tab${
          value === "prices" ? " prices-block-tabs__tab--active" : ""
        }`}
        onClick={() => onChange("prices")}
      >
        מחירים
      </button>
      <button
        type="button"
        role="tab"
        id={offersTabId}
        aria-selected={value === "offers"}
        aria-controls={offersPanelId}
        aria-label={offersLabel}
        className={`prices-block-tabs__tab${
          value === "offers" ? " prices-block-tabs__tab--active" : ""
        }`}
        onClick={() => onChange("offers")}
      >
        מבצעים
        {offersCount > 0 ? (
          <span className="prices-block-tabs__count" aria-hidden>
            {offersCount}
          </span>
        ) : null}
      </button>
    </div>
  );
};

const SupplierPricesSection = ({
  product,
  onCostPriceChange,
  familyCategory = null,
  sellingPricesHeader = null,
  profitMarginRow = null,
  sellingPriceSaveButton = null,
  promoSaleRow = null,
  promotionsSlot = null,
  sellingOffersCount = 0,
}) => {
  const [purchasePrice, setPurchasePrice] = useState("");
  const [discountPercent, setDiscountPercent] = useState("0");
  const [finalPurchasePrice, setFinalPurchasePrice] = useState("");
  const [purchasePriceWithDeposit, setPurchasePriceWithDeposit] = useState("");
  const [purchasePriceManufacturerPack, setPurchasePriceManufacturerPack] =
    useState("");
  const [savedPurchasePrice, setSavedPurchasePrice] = useState("");
  const [savedDiscountPercent, setSavedDiscountPercent] = useState("0");
  const [publishDate, setPublishDate] = useState("");
  const [supplierPromotions, setSupplierPromotions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingPromoId, setDeletingPromoId] = useState(null);
  const [showFamilyDialog, setShowFamilyDialog] = useState(false);
  const [showSupplierPromoForm, setShowSupplierPromoForm] = useState(false);
  const [editingSupplierPromo, setEditingSupplierPromo] = useState(null);
  const [purchaseTab, setPurchaseTab] = useState("prices");
  const [sellingTab, setSellingTab] = useState("prices");

  const gtin = product?.gtin;
  const supplierId = product?.brand?.id ?? "";
  const depositAmount = getDepositAmount(product);
  const isDepositItem = depositAmount > 0;
  const vatExempt = Boolean(product?.vatExempt);
  const vatOptions = useMemo(() => ({ vatExempt }), [vatExempt]);
  const markedProducts = useMemo(() => [product], [product]);

  const emitCostPrice = useCallback(
    (costBase) => {
      onCostPriceChange?.(toCostForMargin(costBase, depositAmount, vatOptions));
    },
    [depositAmount, onCostPriceChange, vatOptions],
  );

  const syncManufacturerPackFromCost = useCallback(
    (costValue) => {
      if (!getManufacturerPackQuantity(product)) {
        setPurchasePriceManufacturerPack("");
        return;
      }
      if (costValue === "") {
        setPurchasePriceManufacturerPack("");
        return;
      }
      const packPrice = getManufacturerPackPurchasePrice(costValue, product);
      setPurchasePriceManufacturerPack(
        packPrice == null ? "" : String(packPrice),
      );
    },
    [product],
  );

  const syncDepositFromCost = useCallback(
    (costValue) => {
      if (!depositAmount || costValue === "") {
        setPurchasePriceWithDeposit("");
        return;
      }
      const withDeposit = getPriceIncludingDeposit(
        costValue,
        depositAmount,
        vatOptions,
      );
      setPurchasePriceWithDeposit(
        withDeposit == null ? "" : String(withDeposit),
      );
    },
    [depositAmount, vatOptions],
  );

  const syncDerivedFromFinal = useCallback(
    (finalValue) => {
      syncDepositFromCost(finalValue);
      syncManufacturerPackFromCost(finalValue);
    },
    [syncDepositFromCost, syncManufacturerPackFromCost],
  );

  const applyBaseAndDiscount = useCallback(
    (baseValue, discountValue) => {
      setPurchasePrice(baseValue);
      setDiscountPercent(discountValue === "" ? "" : String(discountValue));
      if (baseValue === "") {
        setFinalPurchasePrice("");
        syncDerivedFromFinal("");
        return "";
      }
      const nextFinal = priceAfterDiscount(
        baseValue,
        discountValue === "" ? 0 : discountValue,
      );
      const finalValue = nextFinal == null ? "" : String(nextFinal);
      setFinalPurchasePrice(finalValue);
      syncDerivedFromFinal(finalValue);
      return finalValue;
    },
    [syncDerivedFromFinal],
  );

  const applyFinalValue = useCallback(
    (finalValue, { syncDeposit = true, syncPack = true } = {}) => {
      setFinalPurchasePrice(finalValue);
      if (syncDeposit) syncDepositFromCost(finalValue);
      if (syncPack) syncManufacturerPackFromCost(finalValue);
      if (finalValue === "") {
        return;
      }
      if (purchasePrice === "" || toDiscountNumber(discountPercent) === 0) {
        setPurchasePrice(finalValue);
        setDiscountPercent(discountPercent === "" ? "" : "0");
        return;
      }
      setDiscountPercent(
        String(discountPercentFromPrices(purchasePrice, finalValue)),
      );
    },
    [
      purchasePrice,
      discountPercent,
      syncDepositFromCost,
      syncManufacturerPackFromCost,
    ],
  );

  const loadPurchasePrice = useCallback(async () => {
    if (!gtin || !supplierId) {
      applyBaseAndDiscount("", "0");
      setSavedPurchasePrice("");
      setSavedDiscountPercent("0");
      setPublishDate("");
      setSupplierPromotions([]);
      onCostPriceChange?.(null);
      return;
    }

    setLoading(true);
    try {
      const data = await GetSupplierPrices({
        gtin,
        supplierId,
        includePromotions: true,
      });
      const active = normalizePriceList(data)[0] ?? null;
      const activePrice = active?.price != null ? String(active.price) : "";
      const hasApiDiscount =
        active?.discountPercent != null && active.discountPercent !== "";
      const loadedDiscount = hasApiDiscount
        ? String(active.discountPercent)
        : active?.finalPrice != null && activePrice
          ? String(discountPercentFromPrices(activePrice, active.finalPrice))
          : "0";
      const computedFinal = applyBaseAndDiscount(activePrice, loadedDiscount);
      setSavedPurchasePrice(activePrice);
      setSavedDiscountPercent(loadedDiscount);
      setPublishDate(active?.publishDate || formatSupplierPriceDate());
      setSupplierPromotions(
        dedupeSupplierPromotions(
          active?.allPromotions ?? active?.promotions ?? [],
        ),
      );
      emitCostPrice(computedFinal === "" ? null : computedFinal);
    } catch (error) {
      applyBaseAndDiscount("", "0");
      setSavedPurchasePrice("");
      setSavedDiscountPercent("0");
      setPublishDate("");
      setSupplierPromotions([]);
      onCostPriceChange?.(null);
      console.error("Error loading purchase price:", error);
      showAlert({ msgTitle: "שגיאה בטעינת מחיר הקנייה", msgType: "error" });
    } finally {
      setLoading(false);
    }
  }, [
    gtin,
    supplierId,
    onCostPriceChange,
    applyBaseAndDiscount,
    emitCostPrice,
  ]);

  useEffect(() => {
    loadPurchasePrice();
  }, [loadPurchasePrice]);

  const handlePurchasePriceChange = (value) => {
    const nextFinal = applyBaseAndDiscount(value, discountPercent);
    emitCostPrice(nextFinal === "" ? null : nextFinal);
  };

  const handleDiscountPercentChange = (value) => {
    const nextFinal = applyBaseAndDiscount(purchasePrice, value);
    emitCostPrice(nextFinal === "" ? null : nextFinal);
  };

  const handleFinalPurchasePriceChange = (value) => {
    applyFinalValue(value);
    emitCostPrice(value === "" ? null : value);
  };

  const handlePurchasePriceWithDepositChange = (value) => {
    setPurchasePriceWithDeposit(value);
    if (value === "") {
      setFinalPurchasePrice("");
      setPurchasePrice("");
      setDiscountPercent("");
      syncManufacturerPackFromCost("");
      onCostPriceChange?.(null);
      return;
    }
    const base = getPriceExcludingDeposit(value, depositAmount, vatOptions);
    const nextFinal = base == null ? "" : String(base);
    applyFinalValue(nextFinal, { syncDeposit: false });
    const parsed = parseFloat(value);
    onCostPriceChange?.(Number.isFinite(parsed) ? roundMoney(parsed) : null);
  };

  const handleManufacturerPackChange = (value) => {
    setPurchasePriceManufacturerPack(value);
    if (value === "") {
      setFinalPurchasePrice("");
      setPurchasePrice("");
      setDiscountPercent("");
      syncDepositFromCost("");
      onCostPriceChange?.(null);
      return;
    }
    const base = getPurchasePriceFromManufacturerPack(value, product);
    const nextFinal = base == null ? "" : String(base);
    applyFinalValue(nextFinal, { syncPack: false });
    emitCostPrice(nextFinal === "" ? null : nextFinal);
  };

  const parsedDiscount = toDiscountNumber(discountPercent);
  const parsedBase = parseFloat(purchasePrice);
  const parsedFinal = parseFloat(finalPurchasePrice);
  const discountInvalid =
    discountPercent !== "" && !isValidDiscountPercent(discountPercent);
  const finalExceedsBase =
    Number.isFinite(parsedBase) &&
    Number.isFinite(parsedFinal) &&
    parsedFinal > parsedBase;

  const savePurchasePriceForProducts = async (productsToUpdate) => {
    if (!Number.isFinite(parsedBase) || parsedBase < 0) {
      showAlert({ msgTitle: "מחיר לא תקין", msgType: "error" });
      return;
    }
    if (!isValidDiscountPercent(parsedDiscount)) {
      showAlert({
        msgTitle: "אחוז הנחה חייב להיות מספר בין 0 ל-100",
        msgType: "error",
      });
      return;
    }
    if (finalExceedsBase) {
      showAlert({
        msgTitle: "מחיר אחרי הנחה לא יכול להיות גבוה ממחיר הבסיס",
        msgType: "error",
      });
      return;
    }

    setShowFamilyDialog(false);
    setSaving(true);
    try {
      const date = formatSupplierPriceDate();
      await applySequentially(productsToUpdate, async (prod) => {
        const prodGtin = prod?.gtin ?? prod?.Gtin ?? prod?.barcode;
        if (!prodGtin) return;
        await UpdateSupplierPrice({
          supplierId,
          gtin: prodGtin,
          date,
          price: parsedBase,
          discountPercent: parsedDiscount,
        });
      });
      await loadPurchasePrice();
      showAlert({ msgTitle: "מחיר הקנייה עודכן בהצלחה", msgType: "success" });
    } catch (error) {
      console.error("Error updating purchase price:", error);
      showAlert({ msgTitle: "שגיאה בשמירת מחיר הקנייה", msgType: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleSavePurchasePrice = () => {
    if (!supplierId) {
      showAlert({
        msgTitle: "לא נמצא ספק (מותג) עבור המוצר",
        msgType: "error",
      });
      return;
    }

    if (!Number.isFinite(parsedBase) || parsedBase < 0) {
      showAlert({ msgTitle: "מחיר לא תקין", msgType: "error" });
      return;
    }

    if (familyCategory) {
      setShowFamilyDialog(true);
    } else {
      savePurchasePriceForProducts([product]);
    }
  };

  const openNewSupplierPromo = () => {
    setEditingSupplierPromo(null);
    setShowSupplierPromoForm(true);
  };

  const openEditSupplierPromo = (promo) => {
    setEditingSupplierPromo(promo);
    setShowSupplierPromoForm(true);
  };

  const closeSupplierPromoForm = () => {
    setShowSupplierPromoForm(false);
    setEditingSupplierPromo(null);
  };

  const handleDeleteSupplierPromo = async (promo) => {
    const promoId = promo?.promoId;
    if (!promoId || deletingPromoId) return;
    setDeletingPromoId(promoId);
    try {
      await deleteSupplierPromotion(promoId);
      showAlert({ msgTitle: "מבצע הספק נמחק בהצלחה", msgType: "success" });
      await loadPurchasePrice();
    } catch (error) {
      console.error("Error deleting supplier promotion:", error);
      showAlert({
        msgTitle: error?.apiErrorMessage || "שגיאה במחיקת מבצע ספק",
        msgType: "error",
      });
    } finally {
      setDeletingPromoId(null);
    }
  };

  const requestDeleteSupplierPromo = (promo) => {
    showAlertWithConfirmCancel({
      msgTitle: "האם למחוק את מבצע הספק?",
      confirmText: "מחק מבצע",
      cancelText: "חזרה",
      onConfirm: () => handleDeleteSupplierPromo(promo),
    });
  };

  const isUnified = Boolean(
    sellingPricesHeader ||
    profitMarginRow ||
    sellingPriceSaveButton ||
    promoSaleRow ||
    promotionsSlot,
  );

  const purchasePriceUnchanged = isSamePriceValue(
    purchasePrice,
    savedPurchasePrice,
  );
  const discountUnchanged =
    roundMoney(toDiscountNumber(discountPercent)) ===
    roundMoney(toDiscountNumber(savedDiscountPercent));
  const purchasePriceSaveDisabled =
    saving ||
    purchasePrice === "" ||
    !supplierId ||
    discountInvalid ||
    finalExceedsBase ||
    (purchasePriceUnchanged && discountUnchanged);
  const hasManufacturerPack = getManufacturerPackQuantity(product) > 0;
  const canEditSupplierPromos = Boolean(supplierId && gtin);
  const visibleSupplierPromotions = supplierPromotions.filter(
    (promo) => (promo?.status ?? "ACTIVE") !== "EXPIRED",
  );
  const activeSupplierPromo = getActiveSupplierPromotion(
    visibleSupplierPromotions,
  );
  const inclusivePurchasePrice = getPurchasePriceIncludingPromos(
    finalPurchasePrice,
    activeSupplierPromo,
  );
  const showInclusivePurchasePrice =
    inclusivePurchasePrice != null &&
    !isSamePriceValue(inclusivePurchasePrice, finalPurchasePrice);
  const inclusivePurchaseCaption = showInclusivePurchasePrice
    ? activeSupplierPromo?.promoType === SUPPLIER_PROMO_TYPES.X_FOR_Y
      ? `(${formatPrice(inclusivePurchasePrice)} ליח׳)`
      : `(${formatPrice(inclusivePurchasePrice)})`
    : null;

  const renderSupplierPromoItem = (promo) => {
    const statusInfo = SUPPLIER_PROMO_STATUS_MAP[promo.status] || {
      label: promo.status || "פעיל",
      modifier: promo.status ? "" : "active",
    };
    const dateRange = getSupplierPromoDateRange(promo);
    const promoId = promo.promoId;
    const canEdit = promo.status !== "EXPIRED";
    const isDeleting = deletingPromoId != null && deletingPromoId === promoId;

    return (
      <li
        key={promoId || getPromoDedupeKey(promo)}
        className="promo-summary-item"
      >
        <div className="promo-summary-item__top">
          <span className="promo-summary-item__type-tag">
            {getSupplierPromoTypeName(promo.promoType)}
          </span>
          <span className="promo-summary-item__value">
            {getSupplierPromoValue(promo)}
          </span>
          <span
            className={`promo-summary-item__status promo-summary-item__status--${statusInfo.modifier}`}
          >
            {statusInfo.label}
          </span>
          {canEdit && promoId ? (
            <button
              type="button"
              className="promo-summary-item__edit-btn"
              onClick={() => openEditSupplierPromo(promo)}
              aria-label="עריכת מבצע ספק"
              title="עריכת מבצע ספק"
            >
              <Pencil size={16} strokeWidth={2} aria-hidden />
            </button>
          ) : null}
          {promoId ? (
            <button
              type="button"
              className="promo-summary-item__end-btn"
              onClick={() => requestDeleteSupplierPromo(promo)}
              disabled={isDeleting || deletingPromoId != null}
              aria-label="מחק מבצע ספק"
              title="מחק מבצע ספק"
            >
              <Trash2 size={15} strokeWidth={2.25} aria-hidden />
            </button>
          ) : null}
        </div>
        {(promo.supplierDescription || promo.description || dateRange) && (
          <div className="promo-summary-item__bottom">
            {(promo.supplierDescription || promo.description) && (
              <span className="promo-summary-item__desc">
                {promo.supplierDescription || promo.description}
              </span>
            )}
            {dateRange && (
              <span className="promo-summary-item__dates">
                <Calendar size={13} strokeWidth={2} aria-hidden />
                <span className="promo-summary-item__dates-text">
                  {dateRange}
                </span>
              </span>
            )}
          </div>
        )}
      </li>
    );
  };

  const purchaseSaveButton = (
    <button
      type="button"
      className={`ui-btn-brand price-save-btn${
        saving ? " price-save-btn--saving" : ""
      }`}
      disabled={purchasePriceSaveDisabled}
      onClick={handleSavePurchasePrice}
      aria-label="שמור מחיר קנייה"
      title={
        saving
          ? "שומר מחיר קנייה..."
          : purchasePriceUnchanged && discountUnchanged
            ? "אין שינוי לשמירה"
            : discountInvalid
              ? "אחוז הנחה חייב להיות בין 0 ל-100"
              : finalExceedsBase
                ? "מחיר אחרי הנחה לא יכול להיות גבוה ממחיר הבסיס"
                : "שמור מחיר קנייה"
      }
    >
      {saving ? (
        <Loader2 size={20} strokeWidth={2.5} aria-hidden />
      ) : (
        <Check size={20} strokeWidth={2.5} aria-hidden />
      )}
    </button>
  );

  return (
    <>
      <div
        className={`prices-blocks${isUnified ? " prices-unified-section" : ""}`}
      >
        <section className="section-card prices-block prices-block--purchase">
          <h3 className="section-title section-title--compact">מחירי קנייה</h3>
          <PricesBlockTabs
            label="מחירי קנייה"
            value={purchaseTab}
            onChange={setPurchaseTab}
            pricesPanelId="purchase-prices-panel"
            offersPanelId="purchase-offers-panel"
            offersCount={countActivePromotions(visibleSupplierPromotions)}
          />
          <div
            id="purchase-prices-panel"
            role="tabpanel"
            aria-labelledby={tabIdForPanel("purchase-prices-panel")}
            hidden={purchaseTab !== "prices"}
          >
            <div className="prices-fields-row prices-fields-row--purchase-main">
              <div className="field-group">
                <label className="field-label" htmlFor="purchase-base-price">
                  מחיר בסיס
                </label>
                <input
                  id="purchase-base-price"
                  type="number"
                  step="0.01"
                  className="number-input price-input"
                  placeholder="0.00"
                  value={purchasePrice}
                  disabled={saving || loading}
                  onChange={(e) => handlePurchasePriceChange(e.target.value)}
                />
              </div>

              <div className="field-group">
                <label
                  className="field-label"
                  htmlFor="purchase-discount-percent"
                >
                  אחוז הנחה
                </label>
                <div className="input-with-suffix">
                  <input
                    id="purchase-discount-percent"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    className={`number-input${
                      discountInvalid ? " number-input--error" : ""
                    }`}
                    placeholder="0"
                    value={discountPercent}
                    disabled={saving || loading || purchasePrice === ""}
                    onChange={(e) =>
                      handleDiscountPercentChange(e.target.value)
                    }
                  />
                  <span className="input-suffix" aria-hidden>
                    %
                  </span>
                </div>
              </div>

              <div className="field-group field-group--primary">
                <label className="field-label" htmlFor="purchase-price">
                  <span className="field-label__main">
                    {isDepositItem
                      ? "מחיר קנייה סופי ללא פיקדון"
                      : "מחיר קנייה סופי"}
                  </span>
                </label>
                <div className="purchase-final-price-control">
                  <input
                    id="purchase-price"
                    type="number"
                    step="0.01"
                    className={`number-input price-input highlighted${
                      finalExceedsBase ? " number-input--error" : ""
                    }`}
                    placeholder="0.00"
                    value={finalPurchasePrice}
                    disabled={saving || loading || purchasePrice === ""}
                    onChange={(e) =>
                      handleFinalPurchasePriceChange(e.target.value)
                    }
                  />
                  {inclusivePurchaseCaption ? (
                    <span className="purchase-inclusive-price">
                      {inclusivePurchaseCaption}
                    </span>
                  ) : null}
                </div>
              </div>

              {purchaseSaveButton}
            </div>

            {isDepositItem || hasManufacturerPack ? (
              <div
                className={`prices-fields-row prices-fields-row--purchase-extras${
                  isDepositItem ? " prices-fields-row--with-deposit" : ""
                }${hasManufacturerPack ? " prices-fields-row--with-pack" : ""}`}
              >
                {hasManufacturerPack ? (
                  <div className="field-group">
                    <label
                      className="field-label"
                      htmlFor="purchase-price-manufacturer-pack"
                    >
                      מחיר קנייה למארז יצרן
                    </label>
                    <input
                      id="purchase-price-manufacturer-pack"
                      type="number"
                      step="0.01"
                      className="number-input price-input"
                      placeholder="0.00"
                      value={purchasePriceManufacturerPack}
                      disabled={saving || loading}
                      onChange={(e) =>
                        handleManufacturerPackChange(e.target.value)
                      }
                    />
                  </div>
                ) : null}

                {isDepositItem ? (
                  <div className="field-group field-group--deposit-amount">
                    <label
                      className="field-label"
                      htmlFor="purchase-deposit-amount"
                    >
                      פיקדון
                    </label>
                    <input
                      id="purchase-deposit-amount"
                      type="number"
                      step="0.01"
                      className="number-input price-input"
                      value={formatPrice(depositAmount)}
                      readOnly
                      disabled
                      tabIndex={-1}
                    />
                  </div>
                ) : null}

                {isDepositItem ? (
                  <div className="field-group field-group--final-with-deposit">
                    <label
                      className="field-label"
                      htmlFor="purchase-price-with-deposit"
                    >
                      מחיר סופי כולל פיקדון
                    </label>
                    <input
                      id="purchase-price-with-deposit"
                      type="number"
                      step="0.01"
                      className="number-input price-input"
                      placeholder="0.00"
                      value={purchasePriceWithDeposit}
                      disabled={saving || loading}
                      onChange={(e) =>
                        handlePurchasePriceWithDepositChange(e.target.value)
                      }
                    />
                  </div>
                ) : null}

                <span
                  className="price-save-btn price-save-btn--spacer"
                  data-testid="purchase-extras-save-spacer"
                  aria-hidden
                />
              </div>
            ) : null}

            {!supplierId && (
              <p className="purchase-supplier-required" role="status">
                יש להגדיר ספק למוצר לפני עדכון מחיר הקנייה
              </p>
            )}

            {loading ? (
              <p className="supplier-prices-status">טוען מחיר קנייה...</p>
            ) : null}
          </div>
          <div
            id="purchase-offers-panel"
            role="tabpanel"
            aria-labelledby={tabIdForPanel("purchase-offers-panel")}
            hidden={purchaseTab !== "offers"}
          >
            <div className="prices-subsection prices-subsection--supplier-promos prices-subsection--tab-body">
              <div className="prices-tab-toolbar">
                <button
                  type="button"
                  className="promo-add-btn"
                  onClick={openNewSupplierPromo}
                  disabled={!canEditSupplierPromos || saving || loading}
                  aria-label="יצירת מבצע ספק חדש"
                  title="יצירת מבצע ספק חדש"
                >
                  <Plus size={18} strokeWidth={2.5} aria-hidden />
                </button>
              </div>
              <div className="promo-summary-scroll">
                {visibleSupplierPromotions.length > 0 ? (
                  <ul className="promo-summary-list">
                    {visibleSupplierPromotions.map(renderSupplierPromoItem)}
                  </ul>
                ) : (
                  <p className="promotions-empty-hint">אין מבצעי ספק</p>
                )}
              </div>
            </div>
          </div>
        </section>

        {isUnified ? (
          <section className="section-card prices-block prices-block--selling">
            <h3 className="section-title section-title--compact">
              מחירי מכירה
            </h3>
            <PricesBlockTabs
              label="מחירי מכירה"
              value={sellingTab}
              onChange={setSellingTab}
              pricesPanelId="selling-prices-panel"
              offersPanelId="selling-offers-panel"
              offersCount={sellingOffersCount}
            />
            <div
              id="selling-prices-panel"
              role="tabpanel"
              aria-labelledby={tabIdForPanel("selling-prices-panel")}
              hidden={sellingTab !== "prices"}
            >
              {sellingPricesHeader ||
              profitMarginRow ||
              sellingPriceSaveButton ? (
                <div className="prices-fields-row prices-fields-row--selling">
                  {sellingPricesHeader}
                  {profitMarginRow}
                  {sellingPriceSaveButton}
                </div>
              ) : null}
              {promoSaleRow ? (
                <div className="prices-fields-row prices-fields-row--promo-sale">
                  {promoSaleRow}
                </div>
              ) : null}
            </div>
            <div
              id="selling-offers-panel"
              role="tabpanel"
              aria-labelledby={tabIdForPanel("selling-offers-panel")}
              hidden={sellingTab !== "offers"}
            >
              {promotionsSlot ? (
                <div className="prices-subsection prices-subsection--promotions prices-subsection--tab-body">
                  {promotionsSlot}
                </div>
              ) : (
                <p className="promotions-empty-hint">אין מבצעים</p>
              )}
            </div>
          </section>
        ) : null}
      </div>

      {showFamilyDialog && familyCategory && (
        <FamilyChangeDialog
          product={product}
          close={() => setShowFamilyDialog(false)}
          onConfirm={savePurchasePriceForProducts}
          markedProducts={markedProducts}
          massage="סמן את המוצרים שתרצה לעדכן את מחיר הקנייה בנוסף אליו:"
          slug={familyCategory.slug}
        />
      )}

      {showSupplierPromoForm && (
        <PopUp width="560px" close={closeSupplierPromoForm}>
          <SupplierPromotionForm
            promo={editingSupplierPromo}
            supplierId={supplierId}
            gtin={gtin}
            publishDate={publishDate || formatSupplierPriceDate()}
            close={closeSupplierPromoForm}
            onSaved={loadPurchasePrice}
          />
        </PopUp>
      )}
    </>
  );
};

export default SupplierPricesSection;
